import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Config, InputType } from '../config/appConstants';
import { UrlConstants } from '../config/urlConstants';
import { DepositInfo } from '../model/depositInfo.model';
import { BonusRestrictionComponent } from '../quick-deposit/betslip/common/bonus-restriction/bonus-restriction.component';
import { BehaviorDataService } from './behaviorData.service';
import { BetslipQdApiService } from './betslipqd-api.service';
import { DataService } from './data.service';
import { DepositService } from './deposit.service';
import { GtmService } from './gtm.service';

@Injectable({
    providedIn: 'root',
})
export class BonusService {
    depositInfo: DepositInfo;
    showBonusMessage: boolean;
    isBonusError: boolean;
    private ignoreTnCOnSubmit: boolean;
    validatedBonusCode: boolean;
    freeSpinsCount: any;
    rewardType: any;
    maxBonusAmount: any;
    userCurrency: any;
    tAndCAccepted: any;
    bonusRedirectUrl: any;
    bonusError: string;
    minAmount: any;
    isBonusValidationOnSubmit: boolean;
    winSpin: boolean;

    constructor(
        private apiService: BetslipQdApiService,
        private gtmService: GtmService,
        private dataService: DataService,
        private depositService: DepositService,
        private matDialog: MatDialog,
        private behaviorData: BehaviorDataService,
    ) {}

    showAlertPopup() {
        this.matDialog.open(BonusRestrictionComponent, { width: '300px' });
        this.gtmService.raiseEvent('quickDepositLoad', 'PayPal_GamingBonusAlert', this.dataService.depositInfo.paymentMethod);
    }

    handleBonusAmount(depositInfo: DepositInfo) {
        this.depositInfo = depositInfo;
        const nativeAmount = depositInfo?.amountInfo?.nativeAmountString;
        if (nativeAmount && !isNaN(nativeAmount)) {
            this.depositInfo.amountInfo.nativeAmountString = parseFloat(nativeAmount).toFixed(2);
        }
        const { bonusCode, nativeCurrency, nativeAmountString, paymentMethod } = depositInfo?.amountInfo ?? {};

        this.showBonusMessage = false;
        this.isBonusError = false;

        if (paymentMethod === InputType.FLASH1VOUCHER) {
            this.validatedBonusCode = bonusCode;
            this.depositService.resizeCashier();
        } else if (bonusCode && nativeCurrency && nativeAmountString) {
            const requestData = {
                bonusCode,
                txnAmount: { currencyCode: nativeCurrency, value: parseFloat(nativeAmountString) },
                paymentMethod,
            };
            this.getBonusAmount(requestData);
        } else {
            this.behaviorData.changebonusTnC(true);
            this.depositService.resizeCashier();
        }
    }

    private getBonusAmount(requestData: { bonusCode: any; txnAmount: { currencyCode: any; value: number }; paymentMethod: any }) {
        const queryString = `?language=${this.dataService?.sessionInfo?.LANG_ID}`;
        this.apiService.post(UrlConstants.VALIDATE_BONUS_URI + queryString, requestData).subscribe(
            (response) => this.handleBonusResponse(response),
            (rejected) => this.handleBonusResponse(rejected.data),
        );
    }

    private handleBonusResponse(response: BonusResponse) {
        this.ignoreTnCOnSubmit = false;
        if (response) {
            this.validatedBonusCode = this.depositInfo?.amountInfo?.bonusCode;
            this.depositInfo.bonusInfo.bonusId = response?.bonusId;

            if (response?.bonusValidationErrorResponse) {
                this.handleBonusErrorResponse(response);
            } else {
                this.handleValidBonusResponse(response);
            }
        }
        if (this.isBonusValidationOnSubmit) {
            const isBonusValid = this.isBonusValidForSubmit();
            this.handleBonusResponseOnSubmit(isBonusValid);
        } else {
            this.depositService.resizeCashier();
        }
    }

    private handleValidBonusResponse(response: any): void {
        this.showBonusMessage = true;
        this.freeSpinsCount = response.freeSpinsCount;
        this.rewardType = response.rewardType;
        this.maxBonusAmount = response.bonusAmount.value;
        this.userCurrency = response.bonusAmount.currencyCode;
        this.tAndCAccepted = response.tAndCAccepted;
        this.bonusRedirectUrl = response.bonusRedirectUrl;
        this.winSpin = response.winSpin === Config.YES;

        if (!response?.tAndCAccepted) {
            this.depositInfo.bonusInfo.tncFromBonusService = response?.tAndCAccepted?.toString();
        }

        if (!this.tAndCAccepted && this.showBonusMessage && !this.depositService.depositInfo.bonusInfo.bonusTnCAccepted) {
            this.behaviorData.changebonusTnC(false);
        }
    }

    private handleBonusErrorResponse(response: any): void {
        this.behaviorData.changebonusTnC(true);
        this.bonusError = response.bonusValidationErrorResponse?.errorCode;

        if (['803', '806', '807'].includes(this.bonusError)) {
            this.isBonusError = true;
        } else {
            this.ignoreTnCOnSubmit = true;
        }

        if (this.bonusError === '803') {
            this.userCurrency = response.bonusValidationErrorResponse?.minDepositRequired?.currencyCode;
            this.minAmount = response.bonusValidationErrorResponse?.minDepositRequired?.value;
        } else if (this.bonusError === '808' && this.isBonusValidationOnSubmit) {
            this.showAlertPopup();
            return;
        }
    }

    private isBonusValidForSubmit(): boolean {
        return (
            this.depositInfo?.amountInfo?.bonusCode ||
            (!this.isBonusError &&
                (this.ignoreTnCOnSubmit ||
                    this.depositInfo?.bonusInfo?.tncFromBonusService === 'true' ||
                    this.depositInfo?.bonusInfo?.bonusTnCAccepted === true))
        );
    }

    private handleBonusResponseOnSubmit(isBonusValid: boolean): void {
        this.isBonusValidationOnSubmit = false;
        if (isBonusValid) {
            this.depositService.submitDeposit();
        }
    }
}
interface BonusResponse {
    bonusId: any;
    bonusValidationErrorResponse: {
        errorCode: any;
        minDepositRequired: { currencyCode: any; value: any };
    };
    freeSpinsCount: any;
    rewardType: any;
    bonusAmount: { value: any; currencyCode: any };
    tAndCAccepted: { toString: () => any };
    bonusRedirectUrl: any;
    noOfGoldenCoins: any;
    winSpin: string;
    bonusPageUrl: any;
}
